import type { AxiosError } from 'axios';
import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import authService, { LoginRequest, RegisterRequest } from "@/services/auth.service";
import memberInvitationService from "@/services/member-invitation.service";
import userService, { ProfileResponse } from "@/services/user.service";
import { budgetPeriodService } from "@/services/financial.service";
import { useActivityTimeout } from "@/hooks/use-activity-timeout";
import {
  clearSelectedBudgetPeriodId,
  setSelectedBudgetPeriodId,
} from "@/lib/budget-period-session";
import { toast } from "@/hooks/use-toast";
import { getAdminPanelBaseUrl } from "@/lib/panelUrls";
import { getLoginErrorMessage, SessionStartError } from "@/lib/auth-errors";
import { getAccessToken, touchActivity } from "@/lib/api";
import {
  ADMIN_IMPERSONATION_TOKEN_PARAM,
  CHANGE_PASSWORD_ROUTE,
  LOGIN_ROUTE,
  POST_LOGIN_ROUTE,
} from "@/lib/auth-routes";

interface LoginResult {
  success: boolean;
  requires2FA?: boolean;
  tempToken?: string;
  email?: string;
}

interface LogoutOptions {
  /**
   * Rota de destino após encerrar a sessão (padrão: tela de login). Ignorada
   * quando há retorno de impersonação admin pendente — sair de uma sessão
   * impersonada sempre devolve ao painel admin.
   */
  redirectTo?: string;
}

interface AuthContextType {
  user: ProfileResponse | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<LoginResult>;
  verify2FA: (tempToken: string, code: string, rememberMe?: boolean) => Promise<boolean>;
  loginWithAdminImpersonation: (impersonationToken: string, adminReturnUrl?: string | null) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  acceptMemberInvitation: (token: string, newPassword: string, confirmPassword: string) => Promise<void>;
  logout: (options?: LogoutOptions) => Promise<void>;
  refreshProfile: () => Promise<boolean>;
  dismissFirstAccessTutorial: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const ADMIN_IMPERSONATION_RETURN_URL_KEY = "admin_impersonation_return_url";

const resolveAdminReturnUrl = (rawReturnUrl?: string | null): string => {
  const fallbackUrl = getAdminPanelBaseUrl();
  if (!rawReturnUrl || rawReturnUrl.trim().length === 0) {
    return fallbackUrl;
  }

  try {
    const fallbackOrigin = new URL(fallbackUrl).origin;
    const parsedReturnUrl = new URL(rawReturnUrl, fallbackUrl);

    if (parsedReturnUrl.origin !== fallbackOrigin) {
      return fallbackUrl;
    }

    return parsedReturnUrl.toString();
  } catch {
    return fallbackUrl;
  }
};

const setAdminImpersonationReturnUrl = (rawReturnUrl?: string | null): void => {
  localStorage.setItem(ADMIN_IMPERSONATION_RETURN_URL_KEY, resolveAdminReturnUrl(rawReturnUrl));
};

const clearAdminImpersonationReturnUrl = (): void => {
  localStorage.removeItem(ADMIN_IMPERSONATION_RETURN_URL_KEY);
};

const parseJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const tokenParts = token.split(".");
    if (tokenParts.length < 2) {
      return null;
    }

    const payloadSegment = tokenParts[1].replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = payloadSegment.padEnd(Math.ceil(payloadSegment.length / 4) * 4, "=");
    const payloadJson = atob(paddedPayload);

    return JSON.parse(payloadJson) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const getAdminImpersonationFallbackUrl = (): string | null => {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return null;
  }

  const payload = parseJwtPayload(accessToken);
  if (!payload?.impersonated_by_admin_user_id) {
    return null;
  }

  return getAdminPanelBaseUrl();
};

const consumeAdminImpersonationReturnUrl = (): string | null => {
  const storedUrl = localStorage.getItem(ADMIN_IMPERSONATION_RETURN_URL_KEY);
  clearAdminImpersonationReturnUrl();

  if (!storedUrl) {
    return getAdminImpersonationFallbackUrl();
  }

  return resolveAdminReturnUrl(storedUrl);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<ProfileResponse | null>(null);
  const profileSessionVersion = useRef(0);
  // Sem sessão armazenada não há o que validar: visitante entra direto, sem o
  // frame de skeleton dos guards (GuestRoute/ProtectedRoute).
  const [loading, setLoading] = useState<boolean>(() => authService.isAuthenticated());
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * Handle inactivity logout - called by the activity timeout hook
   * when user has been inactive for 30 minutes.
   */
  const handleInactivityLogout = useCallback(() => {
    profileSessionVersion.current += 1;
    const adminReturnUrl = consumeAdminImpersonationReturnUrl();
    setUser(null);
    // Descarta TODO o cache do React Query: dados sensíveis da conta (assinatura,
    // faturas, financeiro) não podem sobreviver à troca de sessão e vazar para o
    // próximo usuário que logar neste navegador.
    queryClient.clear();
    toast({
      title: "Sessão expirada",
      description: "Você foi desconectado por inatividade. Faça login novamente.",
      variant: "destructive",
    });
    if (adminReturnUrl) {
      window.location.assign(adminReturnUrl);
      return;
    }

    navigate(LOGIN_ROUTE);
  }, [navigate, queryClient]);

  // Activity timeout hook - monitors user activity and handles token refresh
  // Only enabled when user is authenticated
  useActivityTimeout({
    onLogout: handleInactivityLogout,
    enabled: !!user,
  });

  useEffect(() => {
    const initialize = async () => {
      try {
        if (authService.isAuthenticated()) {
          const profile = await userService.getMyProfile();
          setUser(profile);
          
          touchActivity();

          if (profile.must_change_password) {
            // Troca de impersonação em curso na tela de login: o exchange vai
            // substituir esta sessão; navegar agora descartaria o token da URL.
            const impersonacaoEmCurso =
              window.location.pathname === LOGIN_ROUTE &&
              Boolean(
                new URLSearchParams(window.location.search).get(ADMIN_IMPERSONATION_TOKEN_PARAM),
              );
            if (!impersonacaoEmCurso) {
              navigate(CHANGE_PASSWORD_ROUTE);
            }
            return;
          }

          try {
            const currentPeriod = await budgetPeriodService.navigate('current');
            setSelectedBudgetPeriodId(profile.id, currentPeriod.id);
          } catch (navError) {
            console.warn("Failed to navigate to current budget period:", navError);
            clearSelectedBudgetPeriodId(profile.id);
          }
        }
      } catch (error) {
        console.error("Erro ao carregar usuário:", error);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);

  const login = async (
    email: string,
    password: string,
    rememberMe = false,
  ): Promise<LoginResult> => {
    profileSessionVersion.current += 1;
    const payload: LoginRequest = { email, password };

    try {
      const response = await authService.login(payload, rememberMe);

      // Check if 2FA is required
      if (response.requires_2fa) {
        return {
          success: false,
          requires2FA: true,
          tempToken: response.temp_token,
          email: email,
        };
      }

      // Nova sessão: descarta qualquer cache residual de uma conta anterior
      // (defesa em profundidade caso o logout não tenha sido executado).
      queryClient.clear();
      const profile = await userService.getMyProfile();
      setUser(profile);
      clearAdminImpersonationReturnUrl();
      touchActivity();

      if (profile.must_change_password) {
        navigate(CHANGE_PASSWORD_ROUTE);
        return { success: true };
      }

      try {
        const currentPeriod = await budgetPeriodService.navigate('current');
        setSelectedBudgetPeriodId(profile.id, currentPeriod.id);
      } catch (navError) {
        console.warn("Failed to navigate to current budget period:", navError);
        clearSelectedBudgetPeriodId(profile.id);
      }

      toast({
        title: "Login realizado com sucesso!",
        description: "Bem-vindo de volta.",
      });

      navigate(POST_LOGIN_ROUTE);
      return { success: true };
    } catch (error: unknown) {
      // Falha de credenciais (401): não expomos o motivo exato por segurança
      // (não revela se o e-mail existe) e cobrimos todos os cenários possíveis —
      // e-mail/senha incorretos, conta inexistente ou excluída por inatividade.
      // A mesma mensagem é reutilizada inline na LoginPage (getLoginErrorMessage).
      const { title, description } = getLoginErrorMessage(error);
      toast({ title, description, variant: "destructive" });
      throw error;
    }
  };

  const verify2FA = async (
    tempToken: string,
    code: string,
    rememberMe = false,
  ): Promise<boolean> => {
    profileSessionVersion.current += 1;
    try {
      await authService.verify2FA(tempToken, code, rememberMe);

      // Nova sessão: descarta cache residual de uma conta anterior.
      queryClient.clear();
      const profile = await userService.getMyProfile();
      setUser(profile);
      clearAdminImpersonationReturnUrl();
      touchActivity();

      if (profile.must_change_password) {
        navigate(CHANGE_PASSWORD_ROUTE);
        return true;
      }

      try {
        const currentPeriod = await budgetPeriodService.navigate('current');
        setSelectedBudgetPeriodId(profile.id, currentPeriod.id);
      } catch (navError) {
        console.warn("Failed to navigate to current budget period:", navError);
        clearSelectedBudgetPeriodId(profile.id);
      }

      toast({
        title: "Login realizado com sucesso!",
        description: "Bem-vindo de volta.",
      });

      navigate(POST_LOGIN_ROUTE);
      return true;
    } catch (error: unknown) {
      toast({
        title: "Código inválido",
        description: (error as AxiosError<{ detail?: string }>)?.response?.data?.detail ?? "Verifique o código e tente novamente.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const loginWithAdminImpersonation = async (
    impersonationToken: string,
    adminReturnUrl?: string | null,
  ): Promise<void> => {
    profileSessionVersion.current += 1;
    try {
      await authService.exchangeAdminImpersonationToken(impersonationToken);

      // Entrando em outra conta (impersonação admin): descarta cache residual.
      queryClient.clear();
      const profile = await userService.getMyProfile();
      setUser(profile);
      setAdminImpersonationReturnUrl(adminReturnUrl);

      try {
        const currentPeriod = await budgetPeriodService.navigate('current');
        setSelectedBudgetPeriodId(profile.id, currentPeriod.id);
      } catch (navError) {
        console.warn("Failed to navigate to current budget period:", navError);
        clearSelectedBudgetPeriodId(profile.id);
      }

      toast({
        title: "Acesso administrativo liberado",
        description: "Você entrou no painel do assinante.",
      });

      navigate(POST_LOGIN_ROUTE);
    } catch (error: unknown) {
      const message = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail ?? "Não foi possível acessar a conta do assinante.";
      toast({
        title: "Erro ao acessar conta",
        description: message,
        variant: "destructive",
      });
      clearAdminImpersonationReturnUrl();
      throw error;
    }
  };

  // Sessão recém-criada pela API (cadastro ou aceite de convite): os tokens já
  // foram persistidos pelo serviço. Populamos o contexto (user + período atual)
  // — mesmo fluxo do login — para o ProtectedRoute reconhecer a sessão e o
  // usuário entrar direto no app, sem passar pela tela de login.
  const startIssuedSession = async () => {
    // Nova sessão: descarta qualquer cache residual de uma conta anterior.
    queryClient.clear();
    const profile = await userService.getMyProfile();
    setUser(profile);
    clearAdminImpersonationReturnUrl();
    touchActivity();

    try {
      const currentPeriod = await budgetPeriodService.navigate('current');
      setSelectedBudgetPeriodId(profile.id, currentPeriod.id);
    } catch (navError) {
      console.warn("Failed to navigate to current budget period:", navError);
      clearSelectedBudgetPeriodId(profile.id);
    }
  };

  const register = async (data: RegisterRequest) => {
    profileSessionVersion.current += 1;
    await authService.register(data);
    await startIssuedSession();
  };

  const acceptMemberInvitation = async (
    token: string,
    newPassword: string,
    confirmPassword: string,
  ) => {
    profileSessionVersion.current += 1;
    await memberInvitationService.accept(token, newPassword, confirmPassword);
    try {
      await startIssuedSession();
    } catch (error) {
      throw new SessionStartError(error);
    }
  };

  const logout = async (options?: LogoutOptions) => {
    profileSessionVersion.current += 1;
    const adminReturnUrl = consumeAdminImpersonationReturnUrl();
    const currentUserId = user?.id;

    try {
      await authService.logout();
    } catch (error) {
      // Falha no logout remoto não pode prender o usuário logado: seguimos com
      // a limpeza local e a navegação. Antes isto era um `return` dentro do
      // `finally`, que engolia o erro só quando havia adminReturnUrl — sem ele
      // a exceção escapava e virava toast enganoso ou unhandled rejection.
      console.warn("Falha ao encerrar a sessão no servidor:", error);
    }

    clearSelectedBudgetPeriodId(currentUserId);
    setUser(null);
    // Limpa o cache do React Query para que dados da conta encerrada não
    // vazem para o próximo login feito neste navegador.
    queryClient.clear();
    if (adminReturnUrl) {
      window.location.assign(adminReturnUrl);
      return;
    }
    navigate(options?.redirectTo ?? LOGIN_ROUTE);
  };

  const refreshProfile = async (): Promise<boolean> => {
    if (!authService.isAuthenticated()) {
      setUser(null);
      return false;
    }
    const sessionVersion = profileSessionVersion.current;
    try {
      const profile = await userService.getMyProfile();
      if (
        sessionVersion !== profileSessionVersion.current ||
        !authService.isAuthenticated()
      ) return false;
      setUser(profile);
      return true;
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      return false;
    }
  };

  const dismissFirstAccessTutorial = async () => {
    if (!authService.isAuthenticated()) {
      return;
    }

    try {
      const profile = await userService.dismissFirstAccessTutorial();
      setUser(profile);
    } catch (error) {
      console.error("Erro ao registrar fechamento do tutorial inicial:", error);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    verify2FA,
    loginWithAdminImpersonation,
    register,
    acceptMemberInvitation,
    logout,
    refreshProfile,
    dismissFirstAccessTutorial,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
